import { useMemo } from 'react';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { useGetPastSurveys } from '../../hooks/queries/useGetPastSurveys';
import { useGetLatestSurveyByTeamIdQuery } from '../../hooks/queries/useGetLatestSurveyByTeamIdQuery';
import { useGetWorkEnvironmentSurveysByTeamIdQuery } from '../../hooks/queries/useGetWorkEnvironmentSurveysByTeamIdQuery';
import { useGetCustomSurveysByTeamIdQuery } from '../../hooks/queries/useGetCustomSurveysByTeamIdQuery';
import { useGetPlannedSurveys } from '../../hooks/queries/useGetPlannedSurveys';
import { useGetScheduledCustomSurveysQuery } from '../../hooks/queries/useGetScheduledCustomSurveysQuery';
import {
  fromCustom,
  fromPsychologicalSafety,
  fromWorkEnvironment,
  type OngoingSurveyLink,
  type SurveyModule,
  sortSurveys,
  toPlannedSurveyItems,
} from '../../domain/surveys/survey-list';

/** Modulene teamet har tilgang til, i fast rekkefølge. */
export const useEnabledSurveyModules = (): SurveyModule[] => {
  const { hasFeature } = useFeatureFlag();
  return [
    hasFeature('PSYCHOLOGICAL_SAFETY') && 'psychologicalSafety',
    hasFeature('WORK_ENVIRONMENT') && 'workEnvironment',
    hasFeature('CUSTOM_SURVEYS') && 'custom',
  ].filter((module): module is SurveyModule => Boolean(module));
};

/** Alle undersøkelser for teamet, fra modulene teamet har tilgang til. */
export const useSurveyList = (teamId: number) => {
  const modules = useEnabledSurveyModules();
  const teamIdFor = (module: SurveyModule) => (modules.includes(module) ? teamId : 0);

  const psychologicalSafety = useGetPastSurveys(teamIdFor('psychologicalSafety'));
  const ongoing = useGetLatestSurveyByTeamIdQuery(teamIdFor('psychologicalSafety'));
  const workEnvironment = useGetWorkEnvironmentSurveysByTeamIdQuery(teamIdFor('workEnvironment'));
  const custom = useGetCustomSurveysByTeamIdQuery(teamIdFor('custom'));

  const items = useMemo(() => {
    const ongoingLink: OngoingSurveyLink | null =
      ongoing.data?.id && ongoing.data.uuid ? { id: ongoing.data.id, uuid: ongoing.data.uuid } : null;
    return sortSurveys([
      ...(psychologicalSafety.data ?? []).map((survey) =>
        fromPsychologicalSafety(survey, teamId, ongoingLink),
      ),
      ...(workEnvironment.data ?? []).map(fromWorkEnvironment),
      ...(custom.data ?? []).map(fromCustom),
    ]);
  }, [psychologicalSafety.data, workEnvironment.data, custom.data, ongoing.data, teamId]);

  const queries = { psychologicalSafety, workEnvironment, custom };
  const failedModules = modules.filter((module) => queries[module].isError);

  return {
    items,
    modules,
    isLoading: modules.some((module) => queries[module].isLoading),
    failedModules,
    retryFailed: () => failedModules.forEach((module) => queries[module].refetch()),
  };
};

/** Planlagte undersøkelser for teamet. */
export const usePlannedSurveyList = (teamId: number) => {
  const standard = useGetPlannedSurveys(teamId);
  const custom = useGetScheduledCustomSurveysQuery(teamId);

  const items = useMemo(
    () => toPlannedSurveyItems(standard.data, custom.data),
    [standard.data, custom.data],
  );

  return {
    items,
    isLoading: standard.isLoading || custom.isLoading,
    isError: standard.isError || custom.isError,
    retry: () => {
      standard.refetch();
      custom.refetch();
    },
  };
};
